import { useCallback } from "react";

import Box from "@mui/material/Box";
import Button, { ButtonProps } from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import { styled, useTheme } from "@mui/material/styles";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { error } from "app/themes/colors";
import Dialog from "components/dialog";

import {
  resetConfirmDialog,
  selectConfirmDialogContent,
  selectConfirmDialogOnCancel,
  selectConfirmDialogOnChange,
  selectConfirmDialogOpen,
  selectConfirmDialogOptions,
  selectConfirmDialogTitle
} from "./confirm-dialog.slice";

// Exported so surfaces that legitimately cannot use ConfirmDialog itself still get the
// app's danger treatment rather than a hand-rolled near-copy that drifts on the next
// theme change. Today that is RepairDeleteWarningDialog (ENG-1834), which offers three
// actions where this dialog's slice only carries one action + Cancel.
export const DangerButton = styled(Button)<ButtonProps>(({ theme }) => ({
  color: theme.palette.getContrastText(error[600]),
  backgroundColor: error[700],
  "&:hover": {
    backgroundColor: error[800]
  },
  // Without this a disabled danger button stays red and reads as clickable.
  "&.Mui-disabled": {
    color: theme.palette.action.disabled,
    backgroundColor: theme.palette.action.disabledBackground
  }
}));

export interface ConfirmDialogOptions {
  actionButtonType?: string;
  actionButtonText?: string;
  actionButtonDisabled?: boolean;
  hideCancelButton?: boolean;
  zIndex?: number;
}

export default function ConfirmDialog() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const confirmChangeDialogOpen = useAppSelector(selectConfirmDialogOpen);
  const onChange = useAppSelector(selectConfirmDialogOnChange);
  const changeTitle = useAppSelector(selectConfirmDialogTitle);
  const changeContent = useAppSelector(selectConfirmDialogContent);
  const onCancel = useAppSelector(selectConfirmDialogOnCancel);
  const options = useAppSelector(selectConfirmDialogOptions);

  const handleChange = useCallback(() => {
    if (onChange) {
      onChange();
    }
    dispatch(resetConfirmDialog({ title: changeTitle }));
  }, [dispatch, onChange, changeTitle]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
    dispatch(resetConfirmDialog());
  }, [onCancel, dispatch]);

  return (
    <Dialog
      title={changeTitle || "Are you sure you want to continue?"}
      onClose={handleCancel}
      open={confirmChangeDialogOpen}
      dialogContentProps={{
        sx: {
          display: "flex",
          alignItems: "center",
          pb: "20px",
          flexDirection: "column"
        }
      }}
      sx={{
        zIndex: options?.zIndex ?? theme.zIndex.snackbar + 2
      }}
    >
      {changeContent && (
        <Typography component="div" pb={2} variant="body3" width="100%">
          {changeContent}
        </Typography>
      )}
      <DialogActions>
        {!options?.hideCancelButton && (
          <Button
            data-testid="confirm-dialog-cancel"
            variant="outlined"
            size="small"
            onClick={handleCancel}
          >
            Cancel
          </Button>
        )}

        {options?.actionButtonType === "danger" ? (
          <Box
            data-testid="confirm-dialog-yes"
            sx={{ ml: 2 }}
            // The click lands on this wrapper even when the button inside is disabled, so
            // a disabled danger action must not run (or close the dialog).
            onClick={options?.actionButtonDisabled ? undefined : handleChange}
          >
            <DangerButton size="small" disabled={options?.actionButtonDisabled}>
              {options?.actionButtonText ?? "Yes"}
            </DangerButton>
          </Box>
        ) : (
          <Button
            data-testid="confirm-dialog-yes"
            variant="contained"
            size="small"
            onClick={handleChange}
            disabled={options?.actionButtonDisabled}
          >
            {options?.actionButtonText ?? "Yes"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
